'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { useCart, useCurrentUser } from '@akademi/site-builder-runtime';
import { BRAND_NAME } from '../brand';
import { Icon } from '../shared/Icon';
import { items, SkLink, text, type Localized } from '../shared/primitives';
import { useSavedItems } from '../shared/savedItems';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * Site header in three tiers, per the owner-approved site mockup
 * (`spatial/mockups/site`, 2026-09-28):
 * <ol>
 *   <li><b>Utility bar</b>: the shop's promises on the left, utility links and
 *       the phone number on the right. It scrolls away; the rest is sticky.</li>
 *   <li><b>Brand row</b>: logo, a wide search bar, account and basket (with the
 *       basket total).</li>
 *   <li><b>Category bar</b>: the departments, each with an optional mega menu,
 *       and the call-to-action buttons on the right.</li>
 * </ol>
 * Everything comes from the operator's Shell config (see `shellMapping.ts`).
 * Mega menus use the shared `MenuLink.megamenu` shape (`columns` + `featured`),
 * so they are authored in the same Shell Editor as any other theme's.
 *
 * <h3>Mega menus and the keyboard</h3>
 * A department is a real link (to its landing page) plus a separate toggle
 * button that opens its panel: the disclosure-navigation pattern. Pointer
 * hover opens the panel too. Opening a panel on focus alone would put every
 * panel's links into the tab order in turn, so keyboard users would have to
 * walk through five columns per department; the toggle avoids that.
 *
 * <h3>Without JavaScript</h3>
 * Search is a plain GET form (`?q=`). Hover still opens mega menus, and every
 * department link still works. The small-screen menu is a `<details>`
 * disclosure, so it opens and closes with no script.
 *
 * <h3>Accessibility</h3>
 * The skip link is the first focusable element. Icon-only controls carry
 * visually hidden text. Basket and saved counts are part of the link text.
 */
export interface HeaderLink {
  label?: Localized;
  href?: string;
  description?: Localized;
}

export interface HeaderMegaColumn {
  label?: Localized;
  links?: HeaderLink[];
}

export interface HeaderFeatured {
  eyebrow?: Localized;
  title?: Localized;
  description?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  image?: string;
}

export interface HeaderMenuItem {
  label?: Localized;
  href?: string;
  /** Legacy one-level dropdown. Rendered as a single mega column when there are no `columns`. */
  links?: HeaderMenuItem[];
  columns?: HeaderMegaColumn[];
  featured?: HeaderFeatured;
}

export interface HeaderCta {
  label?: Localized;
  href?: string;
  variant?: 'primary' | 'secondary';
}

export interface SpatialKitchenHeaderProps {
  brandName?: Localized;
  /**
   * A short line under the brand name — the shop's motto, not a second name.
   * Spatial Studio's is "The Kitchen Shop". Unset, the name stands alone; it is
   * never shown beside a logo image, which carries its own wording.
   */
  motto?: Localized;
  logo?: string;
  logoHref?: string;
  logoHeight?: number;
  menu?: HeaderMenuItem[];
  /** Short promises in the utility bar, e.g. "Made to measure to the millimetre". */
  usps?: Localized[];
  /** Links on the right of the utility bar, e.g. "Track order", "Trade login". */
  utilityLinks?: HeaderLink[];
  /** Buttons at the right of the category bar, e.g. "Free samples", "Design your kitchen". */
  ctas?: HeaderCta[];
  searchHref?: string;
  searchLabel?: Localized;
  searchPlaceholder?: Localized;
  savedHref?: string;
  savedLabel?: Localized;
  accountHref?: string;
  accountLabel?: Localized;
  signInLabel?: Localized;
  basketHref?: string;
  basketLabel?: Localized;
  menuLabel?: Localized;
  skipLinkLabel?: Localized;
  /** `id` of the element the skip link jumps to. The shell's `<main>` is `lms-main`. */
  skipTargetId?: string;
  sticky?: boolean;
  phone?: string;
  openingHours?: Localized;
}

const CSS = `
.sk-skip{position:absolute;left:1rem;top:-100px;z-index:60;background:var(--sk-ink);color:#fff;padding:.625rem 1rem;border-radius:var(--sk-radius-sm);text-decoration:none;font-weight:600}
.sk-skip:focus{top:.75rem}
.sk-hwrap{width:100%;max-width:var(--sk-max-wide);margin-inline:auto;padding-inline:var(--sk-gutter)}
.sk-topbar{background:var(--sk-sage);color:#E8EFE9;font-size:.8rem;line-height:1.3}
.sk-topbar .sk-hwrap{display:flex;justify-content:space-between;align-items:center;gap:1.5rem;min-height:36px;padding-block:.35rem}
.sk-topbar__usps{display:flex;gap:1.6rem;margin:0;padding:0;list-style:none}
.sk-topbar__usps li{display:inline-flex;align-items:center;gap:.4rem}
.sk-topbar__usps svg{color:var(--sk-brass-soft)}
.sk-topbar__util{display:flex;gap:1.25rem;margin:0 0 0 auto;padding:0;list-style:none}
.sk-topbar a{color:#E8EFE9;text-decoration:none}
.sk-topbar a:hover{text-decoration:underline;color:#fff}
.sk-header{position:relative;z-index:40;background:rgba(247,243,237,.96);border-bottom:1px solid var(--sk-border)}
@supports (backdrop-filter:blur(1px)){.sk-header{backdrop-filter:saturate(1.4) blur(10px)}}
.sk-header--sticky{position:sticky;top:0}
.sk-brandrow{display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center;gap:clamp(.75rem,2.5vw,2.25rem);min-height:72px}
.sk-header__lead{display:flex;align-items:center;gap:.25rem}
.sk-header__logo{display:inline-flex;align-items:center;gap:.625rem;flex:none;color:var(--sk-ink);text-decoration:none;font-family:var(--sk-serif);font-weight:600;font-size:1.45rem;letter-spacing:-.02em;white-space:nowrap}
.sk-header__logo img{display:block;width:auto}
.sk-header__mark{width:32px;height:32px;border-radius:8px;background:var(--sk-sage);display:grid;place-items:center;flex:none}
.sk-header__brand{display:flex;flex-direction:column;justify-content:center;line-height:1.1}
.sk-header__motto{font-family:var(--sk-sans);font-size:.625rem;font-weight:600;letter-spacing:.16em;text-transform:uppercase;color:var(--sk-faint)}
.sk-searchbar{display:flex;align-items:center;gap:.625rem;justify-self:center;width:100%;max-width:720px;height:46px;padding:0 6px 0 18px;background:var(--sk-white);border:1.5px solid var(--sk-stone-deep);border-radius:999px}
.sk-searchbar:focus-within{border-color:var(--sk-sage);box-shadow:0 0 0 3px rgba(46,74,62,.12)}
.sk-searchbar svg{flex:none;color:var(--sk-faint)}
.sk-searchbar input{flex:1;min-width:0;border:0;outline:none;background:transparent;font:inherit;font-size:.95rem;color:var(--sk-ink)}
.sk-searchbar button{flex:none;height:34px;padding:0 1rem;border:0;border-radius:999px;background:var(--sk-sage);color:#fff;font:inherit;font-weight:700;font-size:.85rem;cursor:pointer}
.sk-searchbar button:hover{background:var(--sk-sage-deep)}
.sk-searchrow{display:none;padding-bottom:.75rem}
.sk-tools{display:flex;align-items:center;gap:.25rem}
.sk-tool{position:relative;display:flex;align-items:center;gap:.5rem;padding:.5rem .75rem;border-radius:12px;color:var(--sk-muted);font-size:.78rem;line-height:1.2;text-decoration:none}
.sk-tool:hover{background:var(--sk-stone)}
.sk-tool svg{color:var(--sk-ink)}
.sk-tool b{display:block;color:var(--sk-ink);font-size:.88rem;font-weight:700}
.sk-tool__count{position:absolute;top:2px;left:28px;min-width:18px;height:18px;padding:0 5px;border-radius:9px;background:var(--sk-clay);color:#fff;font-size:.7rem;font-weight:800;line-height:18px;text-align:center}
.sk-icon-btn{position:relative;display:inline-grid;place-items:center;width:2.75rem;height:2.75rem;border-radius:999px;color:var(--sk-ink);text-decoration:none;cursor:pointer;list-style:none;background:transparent;border:0}
.sk-icon-btn::-webkit-details-marker{display:none}
.sk-icon-btn:hover{background:var(--sk-stone)}
.sk-catbar{border-top:1px solid var(--sk-border)}
.sk-catbar .sk-hwrap{display:flex;align-items:stretch;justify-content:space-between;gap:.5rem}
.sk-cat{display:flex;align-items:stretch;gap:2px;margin:0;padding:0;list-style:none}
.sk-cat__item{display:flex;align-items:stretch}
.sk-cat__link{display:flex;align-items:center;height:48px;padding:0 .5rem 0 .875rem;color:var(--sk-muted);font-weight:700;font-size:.9rem;text-decoration:none;white-space:nowrap;border-bottom:2px solid transparent}
.sk-cat__item:not(.sk-cat__item--menu) .sk-cat__link{padding-right:.875rem}
.sk-cat__link:hover,.sk-cat__link[aria-current="page"],.sk-cat__item[data-open] .sk-cat__link{color:var(--sk-ink);border-bottom-color:var(--sk-sage)}
.sk-cat__toggle{display:inline-grid;place-items:center;width:1.75rem;margin-right:.375rem;padding:0;border:0;background:transparent;color:var(--sk-muted);cursor:pointer;border-radius:6px}
.sk-cat__toggle svg{transition:transform .15s}
.sk-cat__item[data-open] .sk-cat__toggle svg{transform:rotate(180deg)}
.sk-cat__toggle:hover{color:var(--sk-ink)}
.sk-mega{position:absolute;left:0;right:0;top:100%;background:var(--sk-white);border-top:1px solid var(--sk-border);box-shadow:0 24px 40px rgba(29,33,31,.12);opacity:0;visibility:hidden;transform:translateY(-4px);transition:opacity .15s,transform .15s,visibility .15s}
.sk-cat__item[data-open] .sk-mega{opacity:1;visibility:visible;transform:none}
@media (hover:hover){.sk-cat__item--menu:hover .sk-mega{opacity:1;visibility:visible;transform:none}}
.sk-mega .sk-hwrap{display:grid;grid-template-columns:repeat(var(--sk-mega-cols,4),minmax(0,1fr)) minmax(0,1.3fr);gap:2rem;padding-block:1.75rem 2rem}
.sk-mega--nofeature .sk-hwrap{grid-template-columns:repeat(var(--sk-mega-cols,4),minmax(0,1fr))}
.sk-mega__title{margin:0 0 .875rem;font-size:.72rem;font-weight:800;line-height:1.2;letter-spacing:.14em;text-transform:uppercase;color:var(--sk-brass-text)}
.sk-mega ul{display:grid;gap:.5625rem;margin:0;padding:0;list-style:none;font-size:.92rem}
.sk-mega ul a{color:var(--sk-ink);text-decoration:none}
.sk-mega ul a:hover{color:var(--sk-sage);text-decoration:underline}
.sk-mega__desc{display:block;color:var(--sk-faint);font-size:.8rem}
.sk-mega__promo{display:flex;flex-direction:column;gap:.5rem;padding:1.25rem;border-radius:var(--sk-radius-card);background:var(--sk-stone);color:var(--sk-ink);text-decoration:none}
.sk-mega__promo img{width:100%;height:auto;border-radius:10px}
.sk-mega__promo strong{font-family:var(--sk-serif);font-size:1.2rem;font-weight:500;line-height:1.25}
.sk-mega__promo .sk-mega__promo-body{color:var(--sk-muted);font-size:.9rem}
.sk-navcta{display:flex;align-items:center;gap:.625rem;flex:none}
.sk-drawer{display:block}
.sk-drawer__panel{position:absolute;left:0;right:0;top:100%;max-height:calc(100vh - 72px);overflow:auto;background:var(--sk-white);border-bottom:1px solid var(--sk-border);box-shadow:0 24px 40px rgba(29,33,31,.12);padding-block:.5rem 1.5rem}
.sk-drawer__list{margin:0;padding:0;list-style:none}
.sk-drawer__list > li{border-bottom:1px solid var(--sk-border)}
.sk-drawer__list a,.sk-drawer__group > summary{display:flex;justify-content:space-between;align-items:center;padding:.875rem 0;color:var(--sk-ink);text-decoration:none;font-size:1.0625rem;font-weight:600;cursor:pointer;list-style:none}
.sk-drawer__group > summary::-webkit-details-marker{display:none}
.sk-drawer__group[open] > summary svg{transform:rotate(180deg)}
.sk-drawer__sub{margin:0 0 .75rem;padding:0 0 0 1rem;list-style:none}
.sk-drawer__sub a{padding:.5rem 0;font-size:.95rem;font-weight:500;color:var(--sk-muted)}
.sk-drawer__subtitle{margin:.75rem 0 .25rem;font-size:.7rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:var(--sk-brass-text)}
.sk-drawer__ctas{display:grid;gap:.625rem;margin-top:1.25rem}
.sk-drawer__util{display:flex;flex-wrap:wrap;gap:.5rem 1.25rem;margin:1.25rem 0 0;padding:0;list-style:none;font-size:.9rem}
.sk-drawer__util a{color:var(--sk-sage)}
.sk-catbar,.sk-topbar__usps{display:none}
@media (min-width:1200px){
  .sk-catbar{display:block}
  .sk-drawer{display:none}
}
@media (min-width:900px){.sk-topbar__usps{display:flex}}
@media (max-width:899px){
  .sk-brandrow{grid-template-columns:auto 1fr auto;min-height:64px}
  .sk-brandrow .sk-searchbar{display:none}
  .sk-searchrow{display:block}
  .sk-searchrow .sk-searchbar{max-width:none}
  .sk-tool .sk-tool__text{display:none}
  .sk-topbar__util{margin:0 auto}
}
@media (max-width:479px){
  .sk-header__logo{font-size:1.2rem}
  .sk-header__mark{display:none}
  .sk-header__motto{font-size:.55rem;letter-spacing:.06em}
  .sk-topbar__util li:not(:last-child){display:none}
}
`;

/** The small square brand mark from the mockup, drawn in the theme's own colour. */
function BrandMark() {
  return (
    <span className="sk-header__mark" aria-hidden="true">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.2} focusable="false">
        <rect x="4" y="4" width="7" height="16" rx="1" />
        <rect x="13" y="4" width="7" height="7" rx="1" />
        <rect x="13" y="13" width="7" height="7" rx="1" />
      </svg>
    </span>
  );
}

/** The columns a department's panel shows: authored columns, else the legacy links as one column. */
export function megaColumns(item: HeaderMenuItem): HeaderMegaColumn[] {
  const columns = items<HeaderMegaColumn>(item.columns).filter((column) => items<HeaderLink>(column.links).length > 0);
  if (columns.length > 0) return columns;
  const links = items<HeaderMenuItem>(item.links);
  return links.length > 0 ? [{ links }] : [];
}

function hasFeatured(featured: HeaderFeatured | undefined): featured is HeaderFeatured {
  return Boolean(featured && (text(featured.title) || featured.image));
}

function formatMoney(amount: string | undefined, currency: string | undefined): string | null {
  const value = Number(amount);
  if (!amount || !Number.isFinite(value)) return null;
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(value);
  } catch {
    return null;
  }
}

function SearchBar({ action, label, placeholder }: { action: string; label: string; placeholder: string }) {
  const id = useId();
  return (
    <form className="sk-searchbar" role="search" method="get" action={action}>
      <Icon name="search" size={18} />
      <label htmlFor={id} className="sk-visually-hidden">
        {label}
      </label>
      <input id={id} type="search" name="q" placeholder={placeholder} autoComplete="off" />
      <button type="submit">{label}</button>
    </form>
  );
}

function MegaPanel({ item, id }: { item: HeaderMenuItem; id: string }) {
  const columns = megaColumns(item);
  const featured = hasFeatured(item.featured) ? item.featured : undefined;
  return (
    <div
      id={id}
      className={`sk-mega${featured ? '' : ' sk-mega--nofeature'}`}
      style={{ ['--sk-mega-cols' as string]: String(Math.max(1, Math.min(columns.length, 5))) }}
    >
      <div className="sk-hwrap">
        {columns.map((column, index) => (
          <div key={index}>
            {text(column.label) && <p className="sk-mega__title">{text(column.label)}</p>}
            <ul>
              {items<HeaderLink>(column.links).map((link, linkIndex) => (
                <li key={`${link.href}-${linkIndex}`}>
                  <SkLink href={link.href}>
                    {text(link.label)}
                    {text(link.description) && <span className="sk-mega__desc">{text(link.description)}</span>}
                  </SkLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {featured && (
          <SkLink href={featured.ctaHref || item.href} className="sk-mega__promo">
            {featured.image && <img src={featured.image} alt="" loading="lazy" decoding="async" />}
            {text(featured.eyebrow) && <span className="sk-eyebrow">{text(featured.eyebrow)}</span>}
            {text(featured.title) && <strong>{text(featured.title)}</strong>}
            {text(featured.description) && <span className="sk-mega__promo-body">{text(featured.description)}</span>}
            {text(featured.ctaLabel) && <span className="sk-link-arrow">{text(featured.ctaLabel)}</span>}
          </SkLink>
        )}
      </div>
    </div>
  );
}

function CategoryBar({ menu, ctas }: { menu: HeaderMenuItem[]; ctas: HeaderCta[] }) {
  const baseId = useId();
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="sk-catbar">
      <div className="sk-hwrap">
        <nav aria-label="Departments">
          <ul className="sk-cat">
            {menu.map((item, index) => {
              const label = text(item.label);
              const hasMenu = megaColumns(item).length > 0 || hasFeatured(item.featured);
              const panelId = `${baseId}-mega-${index}`;
              return (
                <li
                  key={`${item.href}-${index}`}
                  className={`sk-cat__item${hasMenu ? ' sk-cat__item--menu' : ''}`}
                  data-open={open === index ? '' : undefined}
                  onMouseLeave={() => setOpen((current) => (current === index ? null : current))}
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                      setOpen((current) => (current === index ? null : current));
                    }
                  }}
                >
                  <SkLink href={item.href} className="sk-cat__link">
                    {label}
                  </SkLink>
                  {hasMenu && (
                    <>
                      <button
                        type="button"
                        className="sk-cat__toggle"
                        aria-expanded={open === index}
                        aria-controls={panelId}
                        onClick={() => setOpen((current) => (current === index ? null : index))}
                      >
                        <Icon name="chevron-down" size={16} />
                        <span className="sk-visually-hidden">{`${label} menu`}</span>
                      </button>
                      <MegaPanel item={item} id={panelId} />
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
        {ctas.length > 0 && (
          <div className="sk-navcta">
            {ctas.map((cta, index) => (
              <SkLink
                key={`${cta.href}-${index}`}
                href={cta.href}
                className={`sk-btn sk-btn--sm ${cta.variant === 'primary' ? 'sk-btn--primary' : 'sk-btn--secondary'}`}
              >
                {text(cta.label)}
              </SkLink>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Drawer({
  menu,
  ctas,
  utilityLinks,
  label,
}: {
  menu: HeaderMenuItem[];
  ctas: HeaderCta[];
  utilityLinks: HeaderLink[];
  label: string;
}) {
  return (
    <details className="sk-drawer">
      <summary className="sk-icon-btn">
        <Icon name="menu" />
        <span className="sk-visually-hidden">{label}</span>
      </summary>
      <div className="sk-drawer__panel">
        <nav className="sk-hwrap" aria-label="Departments">
          <ul className="sk-drawer__list">
            {menu.map((item, index) => {
              const columns = megaColumns(item);
              const itemLabel = text(item.label);
              if (columns.length === 0) {
                return (
                  <li key={`${item.href}-${index}`}>
                    <SkLink href={item.href}>{itemLabel}</SkLink>
                  </li>
                );
              }
              return (
                <li key={`${item.href}-${index}`}>
                  <details className="sk-drawer__group">
                    <summary>
                      {itemLabel}
                      <Icon name="chevron-down" size={18} />
                    </summary>
                    <ul className="sk-drawer__sub">
                      {item.href && (
                        <li>
                          <SkLink href={item.href}>{`All ${itemLabel.toLowerCase()}`}</SkLink>
                        </li>
                      )}
                      {columns.map((column, columnIndex) => (
                        <li key={columnIndex}>
                          {text(column.label) && <p className="sk-drawer__subtitle">{text(column.label)}</p>}
                          <ul className="sk-list-reset">
                            {items<HeaderLink>(column.links).map((link, linkIndex) => (
                              <li key={`${link.href}-${linkIndex}`}>
                                <SkLink href={link.href}>{text(link.label)}</SkLink>
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </details>
                </li>
              );
            })}
          </ul>
          {ctas.length > 0 && (
            <div className="sk-drawer__ctas">
              {ctas.map((cta, index) => (
                <SkLink
                  key={`${cta.href}-${index}`}
                  href={cta.href}
                  className={`sk-btn ${cta.variant === 'primary' ? 'sk-btn--primary' : 'sk-btn--secondary'}`}
                >
                  {text(cta.label)}
                </SkLink>
              ))}
            </div>
          )}
          {utilityLinks.length > 0 && (
            <ul className="sk-drawer__util">
              {utilityLinks.map((link, index) => (
                <li key={`${link.href}-${index}`}>
                  <SkLink href={link.href}>{text(link.label)}</SkLink>
                </li>
              ))}
            </ul>
          )}
        </nav>
      </div>
    </details>
  );
}

function telHref(phone: string): string {
  return `tel:${phone.replace(/\(0\)/g, '').replace(/[^+\d]/g, '')}`;
}

export function SpatialKitchenHeader(props: SpatialKitchenHeaderProps) {
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    // Escape closes an open panel or drawer and returns focus to its control;
    // a pointer press outside the header closes them.
    function close(event: PointerEvent | KeyboardEvent) {
      const header = headerRef.current;
      if (!header) return;
      const escape = event instanceof KeyboardEvent && event.key === 'Escape';
      if (!escape && (event instanceof KeyboardEvent || header.contains(event.target as Node))) return;
      header.querySelectorAll<HTMLDetailsElement>('details[open]').forEach((details) => {
        if (escape && details.contains(document.activeElement)) details.querySelector('summary')?.focus();
        details.open = false;
      });
      if (escape) {
        const toggle = document.activeElement?.closest('.sk-cat__item')?.querySelector<HTMLButtonElement>('.sk-cat__toggle');
        if (toggle?.getAttribute('aria-expanded') === 'true') {
          toggle.focus();
          toggle.click();
        }
      }
    }
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, []);

  const brand = text(props.brandName, BRAND_NAME);
  const motto = text(props.motto, '');
  const menu = items<HeaderMenuItem>(props.menu);
  const usps = (Array.isArray(props.usps) ? props.usps : []).map((usp) => text(usp)).filter(Boolean);
  const utilityLinks = items<HeaderLink>(props.utilityLinks);
  const ctas = items<HeaderCta>(props.ctas);
  const searchLabel = text(props.searchLabel, 'Search');
  const searchPlaceholder = text(props.searchPlaceholder, 'Search doors, cabinets and colours');
  const searchAction = props.searchHref || '/search';
  const savedLabel = text(props.savedLabel, 'Saved');
  const accountLabel = text(props.accountLabel, 'Account');
  const signInLabel = text(props.signInLabel, 'Sign in');
  const basketLabel = text(props.basketLabel, 'Basket');
  const menuLabel = text(props.menuLabel, 'Menu');

  const cart = useCart();
  const user = useCurrentUser();
  const { saved } = useSavedItems();
  const basketCount = cart?.available ? cart.lineCount : 0;
  const basketTotal = basketCount > 0 ? formatMoney(cart?.cart?.total, cart?.cart?.currency) : null;
  const savedCount = saved.length;
  const showTopbar = usps.length > 0 || utilityLinks.length > 0 || Boolean(props.phone);

  return (
    <>
      <a className="sk sk-skip" href={`#${props.skipTargetId || 'lms-main'}`}>
        {text(props.skipLinkLabel, 'Skip to content')}
      </a>
      {showTopbar && (
        <div className="sk sk-topbar">
          <SkBaseStyles />
          <SkStyles id="header" css={CSS} />
          <div className="sk-hwrap">
            {usps.length > 0 && (
              <ul className="sk-topbar__usps">
                {usps.map((usp, index) => (
                  <li key={index}>
                    <Icon name="check" size={14} />
                    {usp}
                  </li>
                ))}
              </ul>
            )}
            <ul className="sk-topbar__util">
              {utilityLinks.map((link, index) => (
                <li key={`${link.href}-${index}`}>
                  <SkLink href={link.href}>{text(link.label)}</SkLink>
                </li>
              ))}
              {props.phone && (
                <li>
                  <a href={telHref(props.phone)}>
                    {props.phone}
                    {text(props.openingHours) && <span className="sk-visually-hidden">{`, ${text(props.openingHours)}`}</span>}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
      <header
        ref={headerRef}
        className={`sk sk-header${props.sticky === false ? '' : ' sk-header--sticky'}`}
        onClick={(event) => {
          if ((event.target as Element).closest('a')) {
            headerRef.current?.querySelectorAll<HTMLDetailsElement>('details[open]').forEach((details) => {
              details.open = false;
            });
          }
        }}
      >
        <SkBaseStyles />
        <SkStyles id="header" css={CSS} />
        <div className="sk-hwrap sk-brandrow">
          <div className="sk-header__lead">
            <Drawer menu={menu} ctas={ctas} utilityLinks={utilityLinks} label={menuLabel} />
            <SkLink href={props.logoHref || '/'} className="sk-header__logo" aria-label={`${brand}, home`}>
              {props.logo ? (
                <img src={props.logo} alt="" height={props.logoHeight ?? 32} />
              ) : (
                <>
                  <BrandMark />
                  {motto ? (
                    <span className="sk-header__brand">
                      <span>{brand}</span>
                      <span className="sk-header__motto">{motto}</span>
                    </span>
                  ) : (
                    brand
                  )}
                </>
              )}
            </SkLink>
          </div>

          <SearchBar action={searchAction} label={searchLabel} placeholder={searchPlaceholder} />

          <div className="sk-tools">
            {/*
              Only when the shell declares a saved action. It used to fall back to a hardcoded
              href, so a shop that removed the action still got a heart in its header — read as
              a wishlist the shop does not have. The shell decides, as it does for account and basket.
            */}
            {props.savedHref && (
              <SkLink href={props.savedHref} className="sk-tool">
                <Icon name="heart" size={22} />
                <span className="sk-tool__text">
                  <b>{savedLabel}</b>
                  {savedCount > 0 ? `${savedCount} saved` : 'Designs'}
                </span>
                <span className="sk-visually-hidden">{savedCount > 0 ? `, ${savedCount} saved` : ''}</span>
              </SkLink>
            )}

            <SkLink href={props.accountHref || '/account'} className="sk-tool">
              <Icon name="user" size={22} />
              <span className="sk-tool__text">
                <b>{user ? accountLabel : signInLabel}</b>
                {user ? 'Orders & designs' : accountLabel}
              </span>
              <span className="sk-visually-hidden">{user ? '' : `, ${accountLabel}`}</span>
            </SkLink>

            <SkLink href={props.basketHref || '/cart'} className="sk-tool">
              <Icon name="basket" size={22} />
              {basketCount > 0 && (
                <span className="sk-tool__count" aria-hidden="true">
                  {basketCount}
                </span>
              )}
              <span className="sk-tool__text">
                <b>{basketTotal ?? basketLabel}</b>
                {basketTotal ? basketLabel : basketCount > 0 ? '' : 'Empty'}
              </span>
              <span className="sk-visually-hidden">
                {basketCount > 0 ? `, ${basketCount} ${basketCount === 1 ? 'item' : 'items'}` : ''}
              </span>
            </SkLink>
          </div>
        </div>
        <div className="sk-hwrap sk-searchrow">
          <SearchBar action={searchAction} label={searchLabel} placeholder={searchPlaceholder} />
        </div>
        <CategoryBar menu={menu} ctas={ctas} />
      </header>
    </>
  );
}
